import type { InputStatusType } from "@amzn/storm-ui";
import {
    HelpTip,
    Box,
    Text,
    Divider,
    TextArea,
    Button,
    Flex,
    TextButton,
    Tag,
    Pill,
} from "@amzn/storm-ui";
import { useSet } from "@react-hookz/web";
import { capitalize, uniqueId } from "lodash";
import type { ReactNode } from "react";
import { useEffect } from "react";
import React from "react";
import styled from "styled-components";
import { useStore } from "zustand";

import {
    useSetDimensionValue,
    useUpdateStatusOnChange,
} from "../../../hooks/dimension/dimension.hooks";
import {
    DimensionStatus,
    dimensionStatusStore,
} from "../../../store/dimension.status";
import type { ListBuilderDimensionValue } from "../../../store/dimension.value";
import {
    DimensionTypes,
    type DimensionConfiguration,
    type DimensionTemplate,
} from "../../../types/dimensions";
import { buildRequestParameter } from "../../../utils/app.utils";
import { useBatchAsyncWithProgress } from "../../../utils/async.hooks";
import { ComplexDimensionBox, DimensionLabel } from "../common/components";

import { useListBuilderDimensionHydration } from "./ListBuilder.dimension.hydration";

import { ListBuilderValidationAsyncType, validateItem } from ".";
import type { ListBuilderValidators } from ".";

const SideBySideListBuilder = styled.div`
    display: flex;
    border: 1px solid rgb(130, 144, 164);
    border-radius: 5px;
    padding: 10px 5px;
`;

const SideBySideListBuilderContent = styled(Box)`
    border-radius: 0px;
    padding: 5px 15px;
    :last-of-type {
        border-left: 1px solid rgb(130, 144, 164);
    }
`;

const InputFlexBox = styled.div`
    width: 300px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const TextAreaToolbar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

export interface ListBuilderDimensionConfiguration
    extends DimensionConfiguration {
    validator?: ListBuilderValidators;
    description?: string;
    delimiter?: string;
    minItems?: number;
    maxItems?: number;
}

export interface ListBuilderDimensionProps extends DimensionTemplate {
    type: DimensionTypes.LIST_BUILDER;
    configuration: ListBuilderDimensionConfiguration;
}
export const ListBuilderDimension: React.FC<ListBuilderDimensionProps> = (
    props,
) => {
    const {
        configuration,
        optional,
        id,
        dependencies,
        debug,
        requestParameterMap,
    } = props;
    const {
        label,
        tooltip,
        validator,
        description,
        delimiter,
        minItems,
        maxItems,
    } = configuration;

    const [dependencyAllSuccess] = useStore(dimensionStatusStore, (state) => [
        state.checkStatusSuccess(dependencies),
    ]);

    const [input, setInput] = React.useState<string>("");
    const itemsToAdd = React.useMemo(() => {
        return input
            .split("\n")
            .filter((e) => e.trim().length > 0)
            .map((e) => e.trim());
    }, [input]);

    const listSet = useSet<string>();

    const [_dimensionData, setDimensionValue] =
        useSetDimensionValue<ListBuilderDimensionValue>(
            id,
            buildRequestParameter(
                requestParameterMap ?? {
                    value: id,
                },
            ),
        );

    const [validateItemsResult, validateItemsAction] =
        useBatchAsyncWithProgress({
            maxSimultanousCalls:
                validator && ListBuilderValidationAsyncType[validator?.type]
                    ? 10
                    : itemsToAdd.length,
            items: itemsToAdd,
            asyncFn: async (item) => {
                if (listSet.has(item)) {
                    return { item, valid: true, skip: true };
                }
                if (validator && !listSet.has(item)) {
                    const valid = await validateItem(item, validator);
                    if (valid) {
                        listSet.add(item);
                    }
                    return { item, valid };
                } else {
                    listSet.add(item);
                    return { item, valid: true };
                }
            },
        });

    const [validItems, invalidItems] = React.useMemo(() => {
        if (validateItemsResult.result) {
            const validItems: string[] = [];
            const invalidItems: string[] = [];
            for (const { item, valid, skip } of validateItemsResult.result) {
                if (skip) continue;
                if (valid) {
                    validItems.push(item);
                } else {
                    invalidItems.push(item);
                }
            }
            return [validItems, invalidItems];
        }
        return [[], []];
    }, [validateItemsResult.result]);

    const handleAdd = () => {
        void validateItemsAction.execute();
    };

    const handleRemove = (name: string) => {
        listSet.delete(name);
    };

    const handleClearAll = () => {
        validateItemsAction.reset();
        listSet.clear();
    };

    const [validatorStatus, validatorMessage]: [
        InputStatusType | undefined,
        ReactNode,
    ] = React.useMemo(() => {
        let inputStatus: InputStatusType | undefined = undefined;
        let message: ReactNode = "";
        let tooltipMessage: string[] = [];

        if (validateItemsResult.status === "success") {
            if (invalidItems.length > 0) {
                inputStatus = "warning";
                message = `Added ${validItems.length} items, could not validate ${invalidItems.length} items`;
                tooltipMessage = [
                    "Errors:",
                    ...invalidItems.map((i) => `${i}`),
                ];
            } else {
                inputStatus = "success";
                message = `Added ${validItems.length} items`;
            }
        } else if (validateItemsResult.status === "error") {
            inputStatus = "error";
            message = `Error occured during validation: (${validateItemsResult.itemsErroredOnAsync.length}) items`;
        }

        if (tooltipMessage.length > 0) {
            message = (
                <>
                    <Text>{message}</Text>
                    <HelpTip>
                        {tooltipMessage.map((t, i) => (
                            <Text key={i}>{t}</Text>
                        ))}
                    </HelpTip>
                </>
            );
        }
        return [inputStatus, message];
    }, [validateItemsResult]);

    useEffect(() => {
        if (input.length > 0) {
            validateItemsAction.reset();
        }
    }, [input]);

    const isDisabled = !dependencyAllSuccess;

    /**
     * integration with dimension status, value, hydration
     */
    useEffect(() => {
        setDimensionValue({
            type: DimensionTypes.LIST_BUILDER,
            value: delimiter
                ? Array.from(listSet).join(delimiter)
                : JSON.stringify(Array.from(listSet)),
            selectedItems: Array.from(listSet),
        });
    }, [listSet.size]);

    // set status
    useUpdateStatusOnChange(
        id,
        ({ selectedItems }: ListBuilderDimensionValue) => {
            if (selectedItems && selectedItems.length > 0) {
                if (minItems && selectedItems.length < minItems) {
                    return DimensionStatus.INVALID;
                }
                if (maxItems && selectedItems.length > maxItems) {
                    return DimensionStatus.INVALID;
                }
            }
            if (optional) return DimensionStatus.SUCCESS;
            if (selectedItems && selectedItems.length > 0)
                return DimensionStatus.SUCCESS;

            return DimensionStatus.EMPTY;
        },
    );

    useListBuilderDimensionHydration(props, (s) =>
        s.forEach((i) => listSet.add(i)),
    );

    return (
        <ComplexDimensionBox>
            <DimensionLabel
                label={label}
                tooltip={tooltip}
                optional={optional}
            />
            <SideBySideListBuilder>
                <SideBySideListBuilderContent>
                    <Text>Add {capitalize(label)}</Text>
                    <Divider />
                    <InputFlexBox>
                        {description && (
                            <Text color="secondary" size="small">
                                {description}
                            </Text>
                        )}
                        <TextArea
                            id={`${id}-list-builder-dimension`}
                            label
                            hideLabel
                            style={{ height: 100 }}
                            fullWidth
                            onChange={(e) => setInput(e.target.value)}
                            value={input}
                            statusType={validatorStatus}
                            message={validatorMessage as any}
                            disabled={
                                validateItemsResult.status === "loading" ||
                                isDisabled
                            }
                        />
                        <TextAreaToolbar>
                            <Text></Text>
                            <div>
                                <Button
                                    onClick={() => handleAdd()}
                                    loading={
                                        validateItemsResult.status === "loading"
                                    }
                                    disabled={isDisabled}
                                >
                                    {validateItemsResult.status ===
                                    "loading" ? (
                                        <>
                                            <span>Verifying</span>
                                            <span>
                                                {` ${validateItemsResult.completedItemsCount}`}
                                            </span>
                                            <span>/</span>
                                            <span>
                                                {
                                                    validateItemsResult.totalItemCount
                                                }
                                            </span>
                                        </>
                                    ) : (
                                        <span>Add</span>
                                    )}
                                </Button>
                            </div>
                        </TextAreaToolbar>
                    </InputFlexBox>
                </SideBySideListBuilderContent>
                <SideBySideListBuilderContent grow={1} shrink={1} basis="auto">
                    <TextAreaToolbar>
                        <div>
                            <Text>
                                {listSet.size} {capitalize(label)} selected
                            </Text>
                        </div>
                        <Flex rowGap="large">
                            <TextButton
                                disabled={isDisabled}
                                onClick={() => handleClearAll()}
                            >
                                Clear all
                            </TextButton>
                        </Flex>
                    </TextAreaToolbar>
                    <Divider />
                    <Box
                        paddingInlineStart="none"
                        paddingInlineEnd="none"
                        paddingBlockStart="none"
                        paddingBlockEnd="small"
                    >
                        {minItems &&
                            listSet.size < minItems &&
                            listSet.size > 0 && (
                                <Flex alignItems="end">
                                    <Tag variant="error" label="Error" />
                                    <Text size="small" color="secondary">
                                        Minimum {minItems} {label} required
                                    </Text>
                                </Flex>
                            )}
                        {maxItems && listSet.size > maxItems && (
                            <Flex alignItems="end">
                                <Tag variant="error" label="Error" />
                                <Text size="small" color="secondary">
                                    Maximum {maxItems} {label} allowed
                                </Text>
                            </Flex>
                        )}
                    </Box>
                    <Flex wrap="wrap" rowGap="xsmall" columnGap="xsmall">
                        {[...listSet.values()].map((item) => (
                            <Pill
                                key={uniqueId()}
                                label={item}
                                disabled={isDisabled}
                                onClose={() => handleRemove(item)}
                                size="small"
                            />
                        ))}
                    </Flex>
                </SideBySideListBuilderContent>
            </SideBySideListBuilder>
            {debug && (
                <div>
                    <Text>UseBatchAsyncHook details</Text>
                    <pre>{JSON.stringify(validateItemsResult, null, 2)}</pre>
                </div>
            )}
        </ComplexDimensionBox>
    );
};
